// The signed-out pages (sign-in, invitation acceptance; design spec §9 `/login`): the canvas,
// the CompanyOS brand mark and wordmark (founder decision 3), and one 400 px card.
import {
  BrandMark,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  Notice,
  Spinner,
} from "@companyos/ui";
import { MailIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import type { Api } from "../../api/client";

export function AuthFrame({
  title,
  description,
  children,
  footer,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <main
      id="main"
      className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background px-4 py-10 text-foreground"
    >
      <div className="flex items-center gap-3" data-slot="wordmark">
        <BrandMark name="CompanyOS" size={40} />
        <span aria-hidden="true" className="font-heading text-xl font-strong tracking-title">
          CompanyOS
        </span>
      </div>
      <Card className="w-full max-w-[400px]">
        <CardHeader>
          <CardTitle>
            <h1 className="font-heading text-lg font-semibold tracking-[-0.3px]">{title}</h1>
          </CardTitle>
          {description ? <CardDescription>{description}</CardDescription> : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-5">{children}</CardContent>
      </Card>
      {footer ? (
        <div className="max-w-[400px] text-center text-xs text-muted-foreground">{footer}</div>
      ) : null}
    </main>
  );
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * "Email me a sign-in link" (Better Auth magic link): the path invitees use, since they have
 * no password. Opening the link signs in with the email verified and lands on `callbackPath`.
 * In the in-memory API nothing is mailed, so the sent state offers to open the link here.
 */
export function MagicLinkForm({
  api,
  callbackPath,
  defaultEmail = "",
  askName = false,
  submitLabel = "Email me a sign-in link",
  onOpened,
}: {
  api: Api;
  callbackPath: string;
  defaultEmail?: string;
  /** New accounts take their name from here (invitees). */
  askName?: boolean;
  submitLabel?: string;
  /** In-memory API only: the demo link was opened; go to `path`. */
  onOpened: (path: string) => void;
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);
  const valid = EMAIL.test(email.trim());

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!valid) {
      setError(askName ? "Enter the email address you were invited with." : "Enter your email.");
      return;
    }
    setState("sending");
    setError(null);
    try {
      await api.auth.sendMagicLink({
        email: email.trim(),
        ...(askName && name.trim() ? { name: name.trim() } : {}),
        callbackPath,
      });
      setState("sent");
    } catch (e) {
      setState("idle");
      setError(e instanceof Error ? e.message : "The sign-in link could not be sent.");
    }
  }

  if (state === "sent") {
    const token = api.fake?.lastMagicLink(email) ?? null;
    return (
      <div className="flex flex-col gap-3">
        {/* ONBOARD: the same words for every address (known, new, or with two-step sign-in,
            which is mailed nothing), so the page never tells who has an account. */}
        <Notice tone="success" title="Check your email" role="status">
          If {email.trim()} can sign in with a link, one is on its way. It works once and expires in
          a few minutes. Accounts with two-step sign-in use their password and code instead.
        </Notice>
        {api.fake && token ? (
          <Notice
            tone="info"
            title="In-memory API"
            action={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  const path = api.fake?.openMagicLink(token);
                  if (path) onOpened(path);
                }}
              >
                Open the link
              </Button>
            }
          >
            Nothing is mailed in the demo.
          </Notice>
        ) : null}
        <Button variant="ghost" className="self-start" onClick={() => setState("idle")}>
          Use a different email
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <FieldGroup className="gap-4">
        <Field data-invalid={error ? true : undefined}>
          <FieldLabel htmlFor="magic-email">Email</FieldLabel>
          <Input
            id="magic-email"
            type="email"
            autoComplete="email"
            className="h-10"
            value={email}
            aria-invalid={error ? true : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        {askName ? (
          <Field>
            <FieldLabel htmlFor="magic-name">Your name</FieldLabel>
            <Input
              id="magic-name"
              autoComplete="name"
              className="h-10"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <FieldDescription>Optional. Shown on your account.</FieldDescription>
          </Field>
        ) : null}
        {error ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : null}
        <Button type="submit" size="lg" disabled={state === "sending"}>
          {state === "sending" ? <Spinner /> : <MailIcon data-icon="inline-start" />}
          {submitLabel}
        </Button>
      </FieldGroup>
    </form>
  );
}
